import {
  COUNTRY_PHONE_CODES_WITH_FLAGS,
  LAST_USED_LOCATION_STORAGE_KEY,
} from "../constants";

/**
 *
 *  Common utils
 *  Utils
 *
 *  Created by Vladimir Shishlyannikov on 28/02/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

export const loadScript = (src, id, onLoad, async = false) => {
  const script = document.createElement("script");
  var loaded;

  script.src = src;
  script.id = id;
  script.defer = true;
  script.async = async;

  const scriptEl = document.getElementById(id);

  // if (scriptEl /* && typeof google !== 'undefined'*/) {
  //   onLoad();
  // } else {
  script.onload = function () {
    if (loaded) return;

    script.onload = null;

    loaded = true;
    onLoad();
  };

  document.head.appendChild(script);
  // }
};

export const isMobile = (() => {
  if (
    typeof navigator === "undefined" ||
    typeof navigator.userAgent !== "string"
  ) {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();

/**
 * Detects if the device is Android.
 */
export const isAndroidDevice = (() => {
  return (
    typeof window !== "undefined" &&
    window.navigator &&
    window.navigator.userAgent &&
    /android/i.test(navigator.userAgent)
  );
})();

/**
 * Detects if the device is iOS.
 */
export const isIosDevice = (() => {
  return (
    typeof window !== "undefined" &&
    window.navigator &&
    window.navigator.platform &&
    (/iP(ad|hone|od)/.test(window.navigator.platform) ||
      (window.navigator.platform === "MacIntel" &&
        window.navigator.maxTouchPoints > 1))
  );
})();

export const isMac = (() => {
  return (
    typeof window !== "undefined" &&
    window.navigator &&
    window.navigator.platform &&
    window.navigator.platform.toUpperCase().indexOf("MAC") >= 0
  );
})();

// Just strips spaces
export const preparePhoneNumber = (phoneNumber) =>
  `${phoneNumber.replace(/\s/g, "")}`;

export const formatPhoneNumber = (phoneNumber) =>
  phoneNumber
    .replace(/\D/g, "")
    // .replace(/(^[^5])/, '')
    .replace(/(\d{3})(\d)/, "$1 $2")
    .replace(/(\d{3}\s\d{3})(\d{1,4})/, "$1 $2")
    .replace(/(\d{3}\s\d{3}\s\d{4})(\d{1,2})/, "$1 $2");

export function makeUrlString(str = "") {
  return str
    .toLowerCase()
    .replace(/[\s\\/]/g, (match) =>
      match === "\\" || match === "/" ? "" : "-"
    );
}

/**
 * Parses a URL string and extracts its pathname and search parts.
 *
 * @param {string} url - The URL string to parse.
 * @returns {{ pathname: string, search: string }} - An object containing the parsed pathname and search parts.
 */
export function parseUrlParts(url) {
  const parsedUrl = new URL(decodeURIComponent(url), window.location.origin);

  return {
    pathname: parsedUrl.pathname,
    search: parsedUrl.search,
  };
}

// Helper functions
/**
 * Returns the return URL from the location state, or null if not available.
 *
 * @param {Location} location - The location object
 * @returns {string|null} The return URL or null
 */
export function getLocationReturnUrl(location) {
  if (!location || !location.state || !location.state.from) return null;
  const { pathname, search } = location.state.from;
  return `${pathname}${search ? `?${search}` : ""}`;
}

export const clientWidth = () => {
  const documentElement = window && document.documentElement,
    bodyElement = window && document.getElementsByTagName("body")[0];

  if (!window) {
    return 0;
  }

  return (
    window.innerWidth || documentElement.clientWidth || bodyElement.clientWidth
  );
};

/**
 * Wraps promise so it could be cancelled
 * Taken from here https://reactjs.org/blog/2015/12/16/ismounted-antipattern.html
 * @return {Object} with promise and cancel method
 */

export const makeCancelable = (promise) => {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      (val) => (hasCanceled_ ? reject({ isCanceled: true }) : resolve(val)),
      (error) => (hasCanceled_ ? reject({ isCanceled: true }) : reject(error))
    );
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true;
    },
  };
};

export const getCountryInfo = (countryShortName) => {
  const res = COUNTRY_PHONE_CODES_WITH_FLAGS.find((country) => {
    return country.code === countryShortName;
  });

  return res
    ? [res.phoneCode, res.emoji, res.image]
    : [
        "+1",
        "🇺🇸",
        "https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/US.svg",
      ];
};

export const getCountryInfoByPhoneCode = (phoneCode) => {
  const res = COUNTRY_PHONE_CODES_WITH_FLAGS.find((country) => {
    return (
      country.phoneCode === phoneCode &&
      (country.phoneCode === "+1" ? country.code === "US" : true)
    );
  });

  return res
    ? [res.code, res.emoji, res.image]
    : [
        "US",
        "🇺🇸",
        "https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/US.svg",
      ];
};

export const toQueryString = (obj) =>
  obj
    ? Object.entries(obj).reduce((acc, [key, val]) => {
        if (Array.isArray(val)) {
          val.forEach((e) => (acc += (acc ? "&" : "") + key + "=" + e));
        } else {
          acc +=
            (acc ? "&" : "") +
            encodeURIComponent(key) +
            "=" +
            encodeURIComponent(val);
        }

        return acc;
      }, "")
    : "";

export const getBundlesByPartnerId = (bundles, partnerId) => {
  if (!bundles || !bundles.length) {
    return;
  }
  return bundles.filter((b) => b.partnerId === partnerId);
};

/**
 * Retrieves the partner picture url.
 *
 * @param {Object} partner's data
 * @return {String} partner profile picture url
 */
export const getPartnerPictureUrl = (partner) => {
  return partner.photoWithBackground || partner.photo;
};

/**
 * Get last used location storage key for local storage. Now it's just one string, before it was separated by user.
 * Like this `${user ? user.firstName : "default"}/${LAST_USED_LOCATION_STORAGE_KEY}`
 *
 * @param {Object} user's data
 * @return {String} user last used location storage key
 */

export const getLastUsedLocationStorageKey = (user) =>
  `default/${LAST_USED_LOCATION_STORAGE_KEY}`;

/**
 * Retrieves user last used location or default location if no the user has no locations.
 *
 * @param {Object} user's data
 * @return {String} user last used location
 */

export const getLastUsedLocation = (user) => {
  let lastUsedLocation;

  // if (user) {
  //   const storageKey = `${user.firstName}/${LAST_USED_LOCATION_STORAGE_KEY}`;
  const storageKey = getLastUsedLocationStorageKey();

  const data = localStorage.getItem(storageKey);

  if (data !== null) {
    lastUsedLocation = JSON.parse(data);
  }
  // }

  // Disabled for now, as there's a bug when you can't clear the location (as logged-in user)
  // if (!lastUsedLocation) {
  //   const storageKey = `default/${LAST_USED_LOCATION_STORAGE_KEY}`;
  //
  //   lastUsedLocation = JSON.parse(localStorage.getItem(storageKey));
  // }

  return lastUsedLocation;
};

export const getDefaultLastUsedLocation = () => {
  const storageKey = `default/${LAST_USED_LOCATION_STORAGE_KEY}`;

  return JSON.parse(localStorage.getItem(storageKey));
};

export function isValidFirstName(firstName) {
  // Check if firstName is empty or contains "user"
  return !firstName || (!firstName.includes("user") && firstName.length > 1);
}

export function isValidLastName(lastName) {
  // Check if lastName is empty or does not contain only digits
  return !lastName || (!/^\d+$/.test(lastName) && lastName.length > 1);
}

export function generatePassword(length = 12) {
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const password = new Uint8Array(length);
  window.crypto.getRandomValues(password);
  return Array.from(password)
    .map((val) => alphabet[val % alphabet.length])
    .join("");
}
